import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';

import {
  InputBox,
  LbInput,
  LbSelect,
  LbDatePicker,
  HeightInput,
  ReadOnlyInput,
} from '@infoods/lifebase-ui';

import classes from './PersonalInfoStep.module.scss';
import {
  HiddenInput,
  PhoneNumberInput,
} from '../../../../../../../shared/components/form-components';
import {
  validateAge,
  validateEmail,
  validateOnlyLettersAndSpaces,
} from '../../../../../../../shared/utils/reactHookFormUtils';
import { genderOptions } from '../../../../../../../shared/constants/user-info/gender';
import { TermsAcceptCheckbox } from '../../../../../../../shared/components/TermsAcceptCheckbox/TermsAcceptCheckbox';
import { ButtonRow } from '../../../../components/ButtonRow/ButtonRow';
import { challengeSignUpStore } from '../../../../../../stores/challengeSignUpStore';
import {
  ScanIqCustomerSetupPersonalData,
  scanIqCustomerService,
} from '../../../../../../../scan-iq-customer-management';
import { getFilteredTruthyFieldsObj } from '../../../../../../../shared/utils/sortingAndGroupingUtil';
import { challengeSignUpService } from '../../../../../../services/challengeSignUpService';

type FormValues = ScanIqCustomerSetupPersonalData & {
  nickName: string;
};

type PersonalInfoStepProps = {
  onSuccessSubmit: () => void;
};

const PersonalInfoStep = observer(({ onSuccessSubmit }: PersonalInfoStepProps) => {
  const [isMarketingAccepted, setIsMarketingAccepted] = useState(false);
  const [isCommunicationsAccepted, setIsCommunicationsAccepted] = useState(false);

  const { participantId, customer: initialCustomer, setCustomer } = challengeSignUpStore;

  if (!participantId) {
    throw new Error('Participant ID is not defined');
  }

  const { control, register, handleSubmit } = useForm<FormValues>({
    shouldUnregister: true, // remove fields that are not in the form (activeSubscription, setupStatus, etc.)
    defaultValues: getFilteredTruthyFieldsObj(initialCustomer || {}),
  });

  const generalInputProps = {
    control,
    size: 'small',
    variant: 'standard',
  } as const;

  const onSubmit = handleSubmit(async (data) => {
    const { nickName, ...personalData } = data;
    const customer = await scanIqCustomerService.updateCustomer(personalData);
    await challengeSignUpService.setNickname(participantId, nickName);

    setCustomer(customer);

    onSuccessSubmit();
  });

  return (
    <form onSubmit={onSubmit} className={classes.container} noValidate>
      <InputBox title="Personal Data">
        <HiddenInput register={register} name="entityId" />
        <LbInput
          {...generalInputProps}
          name="firstName"
          label="First Name"
          rules={{
            required: 'First Name is required',
            validate: { validateOnlyLettersAndSpaces },
          }}
        />
        <LbInput
          {...generalInputProps}
          name="lastName"
          label="Last Name"
          rules={{
            required: 'Last Name is required',
            validate: { validateOnlyLettersAndSpaces },
          }}
        />
        {initialCustomer?.email ? (
          <ReadOnlyInput {...generalInputProps} name="email" label="Email address" />
        ) : (
          <LbInput
            {...generalInputProps}
            name="email"
            label="Email address"
            type="email"
            rules={{ required: 'Email is required', validate: { validateEmail } }}
          />
        )}
        {initialCustomer?.phone ? (
          <ReadOnlyInput {...generalInputProps} name="phone" label="Cell Phone" />
        ) : (
          <PhoneNumberInput
            {...generalInputProps}
            name="phone"
            label="Cell Phone"
            rules={{ required: 'Cell Phone is required' }}
          />
        )}
        <LbDatePicker
          {...generalInputProps}
          name="dateOfBirth"
          label="Date of Birth"
          helperText="You need to be at least 14 years to join the Challenge"
          openTo="year"
          rules={{
            required: 'Date of Birth is required',
            validate: { validateMinAge: (value) => validateAge(value, 14) },
          }}
        />
        <LbSelect
          {...generalInputProps}
          name="gender"
          label="Gender"
          options={genderOptions}
          rules={{ required: 'Gender is required' }}
        />
        <HeightInput
          {...generalInputProps}
          name="height"
          label="Height"
          rules={{ required: 'Height is required' }}
        />
        <LbInput
          {...generalInputProps}
          name="nickName"
          label="Name displayed on the leaderboard"
          rules={{ required: 'Name displayed on the leaderboard is required' }}
        />
      </InputBox>
      <div>
        <TermsAcceptCheckbox
          isMarketingAccepted={isMarketingAccepted}
          isCommunicationsAccepted={isCommunicationsAccepted}
          setIsMarketingAccepted={setIsMarketingAccepted}
          setIsCommunicationsAccepted={setIsCommunicationsAccepted}
        />
        <ButtonRow yesText="Next Step" />
      </div>
    </form>
  );
});

export { PersonalInfoStep };
